import { useMemo } from "react";
import { Alert, Form, Input, Select, Switch, Typography } from "antd";
import {
  opcionesDeFoco,
  revisarFocoClinico,
} from "../../../../../Utils/focoClinico";

const DIFFICULTY_OPTIONS = [
  "Preventivo",
  "Leve",
  "Moderado",
  "Severo",
  "Todos",
];

// El portal del terapeuta pinta estos textos respetando párrafos y saltos de
// línea, y entiende un marcado mínimo. Se dice aquí, donde se escribe, porque
// hasta el 2026-09-04 se guardaban bien y se pintaban "todo seguido".
const AYUDA_FORMATO =
  "Se respetan los párrafos (línea en blanco) y los saltos de línea. " +
  "**negrita** para resaltar, *cursiva*, y líneas que empiezan por «- » o «1. » para listas. " +
  "También se admite HTML sencillo (<ul>, <li>, <p>, <strong>).";

export function StepClinical({ therapyCats }) {
  const form = Form.useFormInstance();

  const therapyOptions = (therapyCats || []).map((t) => ({
    label: t.title,
    value: String(t._id),
  }));

  // Vocabulario de foco clinico: sale de las categorias que ya estan cargadas,
  // no de una lista escrita aqui. Ver `Utils/focoClinico.js`.
  const focoOptions = useMemo(() => opcionesDeFoco(therapyCats), [therapyCats]);

  const focoActual = Form.useWatch("clinicalFocus", form);
  const revision = useMemo(
    () => revisarFocoClinico(focoActual, therapyCats),
    [focoActual, therapyCats],
  );

  const hayVocabulario = focoOptions.length > 0;

  return (
    <>
      <Form.Item
        name="therapyIds"
        label="Categorías terapéuticas"
        rules={[{ required: true, message: "Selecciona al menos una terapia" }]}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona terapias..."
          options={therapyOptions}
          notFoundContent="No hay datos"
        />
      </Form.Item>

      <Form.Item
        name="premium"
        label="Revi Premium"
        valuePropName="checked"
        initialValue={false}
      >
        <Switch />
      </Form.Item>

      <Alert
        type="warning"
        showIcon
        message="Activar si es una escena de Revi Premium"
        style={{ marginBottom: 16 }}
      />

      <Form.Item
        name="active"
        label="Activo"
        valuePropName="checked"
        initialValue
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name="difficulty"
        label="Dificultad"
        rules={[{ required: true, message: "Selecciona una dificultad" }]}
        initialValue="Todos"
      >
        <Select
          placeholder="Selecciona dificultad..."
          options={DIFFICULTY_OPTIONS.map((d) => ({ label: d, value: d }))}
        />
      </Form.Item>

      <Form.Item
        name="clinicalDescription"
        label="Descripción clínica"
        rules={[
          { required: true, message: "La descripción clínica es obligatoria" },
        ]}
        extra={AYUDA_FORMATO}
      >
        <Input.TextArea
          rows={6}
          placeholder="Texto para el modal/info clínica..."
        />
      </Form.Item>

      <Form.Item
        name="interventionGuide"
        label="Guía de intervención"
        extra={AYUDA_FORMATO}
      >
        <Input.TextArea
          rows={6}
          placeholder="Indicaciones para el terapeuta..."
        />
      </Form.Item>

      <Form.Item
        name="contraindications"
        label="Contraindicaciones"
        extra="Mismo formato que la descripción: párrafos, saltos de línea y **negrita**."
      >
        <Input.TextArea
          rows={3}
          placeholder="Precauciones y contraindicaciones..."
        />
      </Form.Item>

      {/*
        Foco clínico. El campo sigue siendo libre —se puede escribir una
        etiqueta que no esté en la lista— pero ahora la lista se ve, agrupada
        por la categoría que la gobierna, y lo que quede fuera se avisa.

        No se bloquea a propósito: un admin responde de lo que escribe, y
        rechazar obligaría a desplegar cada vez que aparece un concepto clínico
        nuevo. Al auto-tagger sí se le cierra, porque no hay nadie a quien
        avisar (ver sceneAutoTaggerController).
      */}
      <Form.Item
        name="clinicalFocus"
        label="Foco clínico (tags)"
        help={
          hayVocabulario
            ? "Elige de la lista. Puedes escribir una etiqueta nueva, pero no agrupará con ninguna categoría."
            : undefined
        }
      >
        <Select
          mode="tags"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Ej: Memoria, Atención..."
          options={focoOptions}
          notFoundContent={null}
        />
      </Form.Item>

      {revision.corregidas.length > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Se guardarán con la grafía del catálogo"
          description={
            <Typography.Text type="secondary">
              {revision.corregidas
                .map((c) => `«${c.de}» → «${c.a}»`)
                .join(" · ")}
            </Typography.Text>
          }
        />
      ) : null}

      {revision.huerfanas.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            revision.huerfanas.length === 1
              ? "1 etiqueta no pertenece a ninguna categoría"
              : `${revision.huerfanas.length} etiquetas no pertenecen a ninguna categoría`
          }
          description={
            <>
              <div style={{ marginBottom: 4 }}>
                {revision.huerfanas.map((h) => `«${h}»`).join(" · ")}
              </div>
              <Typography.Text type="secondary">
                Se guardan igual, pero no agrupan la sala con ninguna terapia ni
                aparecen en los filtros. Si es un concepto clínico que debería
                existir, hay que darlo de alta en su categoría de terapia.
              </Typography.Text>
            </>
          }
        />
      ) : null}
    </>
  );
}
